// utils/Validator.js
class Validator {
  static isEmail(value) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(value);
  }

  static isURL(value) {
    if (value == null || value === '') return false;
    const s = String(value).trim();
    if (!s) return false;

    const rejectDangerous = (u) => {
      const p = (u.protocol || '').toLowerCase();
      return p === 'javascript:' || p === 'data:' || p === 'vbscript:';
    };

    try {
      const u = new URL(s);
      if (rejectDangerous(u)) return false;
      return true;
    } catch (_) {
      // Protocol-relative URL
      if (s.startsWith('//') && s.length > 2) {
        try {
          const u = new URL(`https:${s}`);
          if (rejectDangerous(u)) return false;
          return Boolean(u.hostname);
        } catch {
          return false;
        }
      }
      // Same-site path only (no host)
      if (s.startsWith('/') && !s.startsWith('//')) {
        try {
          new URL(s, 'https://example.invalid');
          return true;
        } catch {
          return false;
        }
      }
      // Bare host or host/path without scheme (e.g. www.google.com, example.com/about)
      try {
        const normalized = s.replace(/^\/+/, '');
        if (!normalized) return false;
        const u = new URL(`https://${normalized}`);
        if (rejectDangerous(u)) return false;
        return Boolean(u.hostname);
      } catch {
        return false;
      }
    }
  }

  static isNumeric(value) {
    return !isNaN(parseFloat(value)) && isFinite(value);
  }

  static validateNumericPrecision(value, precision, scale = 0) {
    if (!Validator.isNumeric(value)) {
      return { isValid: false, message: 'Invalid numeric value' };
    }

    const numValue = parseFloat(value);
    
    // For decimal fields, calculate the maximum value including decimal places
    let maxValue, minValue;
    if (scale > 0) {
      // For NUMERIC(precision, scale), max value is (10^precision - 1) / 10^scale
      maxValue = (Math.pow(10, precision) - 1) / Math.pow(10, scale);
      minValue = -maxValue;
    } else {
      // For integer fields, max value is 10^precision - 1
      maxValue = Math.pow(10, precision) - 1;
      minValue = -maxValue;
    }

    if (numValue > maxValue || numValue < minValue) {
      return { 
        isValid: false, 
        message: `Value must be between ${minValue} and ${maxValue}` 
      };
    }

    // Check decimal places
    if (scale > 0) {
      const decimalPlaces = (value.toString().split('.')[1] || '').length;
      if (decimalPlaces > scale) {
        return { 
          isValid: false, 
          message: `Value can have at most ${scale} decimal places` 
        };
      }
    }

    return { isValid: true };
  }

  static validate(value, type, options = {}) {
    switch (type) {
      case "email":
        return Validator.isEmail(value);
      case "url":
        return Validator.isURL(value);
      case "number":
      case "decimal":
      case "float":
      case "currency":
        if (options.precision) {
          const result = Validator.validateNumericPrecision(value, options.precision, options.scale);
          return result.isValid;
        }
        return Validator.isNumeric(value);
      default:
        return true; // assume valid for other types
    }
  }

  static getValidationMessage(value, type, options = {}) {
    switch (type) {
      case "email":
        return Validator.isEmail(value) ? '' : 'Invalid email format';
      case "url":
        return Validator.isURL(value) ? '' : 'Invalid URL format';
      case "number":
      case "decimal":
      case "float":
      case "currency":
        if (options.precision) {
          const result = Validator.validateNumericPrecision(value, options.precision, options.scale);
          return result.isValid ? '' : result.message;
        }
        return Validator.isNumeric(value) ? '' : 'Invalid numeric value';
      default:
        return '';
    }
  }
}

export default Validator;
